import React, { useState } from 'react';
import Icon from 'components/AppIcon';

const INITIAL_INVESTORS = [
  { id: 1, name: 'Meridian Capital Partners', type: 'Anchor', commitment: 20, pctEquity: 41.7, ticketType: 'Primary', status: 'Hard-commit', notes: 'Lead anchor investor' },
  { id: 2, name: 'Northern Pension Fund', type: 'LP', commitment: 15, pctEquity: 31.2, ticketType: 'Primary', status: 'Signed', notes: 'Long-term infrastructure mandate' },
  { id: 3, name: 'British Business Bank', type: 'DFI', commitment: 8, pctEquity: 16.7, ticketType: 'Primary', status: 'Soft-circle', notes: 'DFI co-investment facility' },
  { id: 4, name: 'Albion Infrastructure GP', type: 'Strategic', commitment: 5, pctEquity: 10.4, ticketType: 'Secondary', status: 'Soft-circle', notes: 'Strategic co-investor' },
];

const STATUS_CONFIG = {
  'Soft-circle': 'bg-amber-50 text-amber-700',
  'Hard-commit': 'bg-[rgba(59,76,76,0.07)] text-blue-700',
  'Signed': 'bg-emerald-50 text-emerald-700',
};

export default function CoInvestmentTab({ deal, readOnly }) {
  const [investors, setInvestors] = useState(INITIAL_INVESTORS);
  const [proRata, setProRata] = useState(true);
  const [tagAlong, setTagAlong] = useState(true);
  const [rofr, setRofr] = useState(true);
  const [infoRights, setInfoRights] = useState('Full');
  const [boardRep, setBoardRep] = useState('Seat');
  const [transferRestrictions, setTransferRestrictions] = useState('Transfer of interests requires prior written consent of the GP. Lock-up period of 3 years from first close.');

  const targetRaise = deal?.equityTranche || 48;
  const softCircled = investors?.filter((i) => i?.status === 'Soft-circle')?.reduce((s, i) => s + i?.commitment, 0);
  const hardCommitted = investors?.filter((i) => i?.status === 'Hard-commit')?.reduce((s, i) => s + i?.commitment, 0);
  const signed = investors?.filter((i) => i?.status === 'Signed')?.reduce((s, i) => s + i?.commitment, 0);
  const totalCommitted = softCircled + hardCommitted + signed;
  const totalPct = targetRaise > 0 ? Math.min((totalCommitted / targetRaise) * 100, 100) : 0;

  const addInvestor = () => {
    if (readOnly) return;
    setInvestors((prev) => [
      ...prev,
      { id: Date.now(), name: 'New Investor', type: 'LP', commitment: 0, pctEquity: 0, ticketType: 'Primary', status: 'Soft-circle', notes: '' },
    ]);
  };

  const Toggle = ({ value, onChange, label }) => (
    <div className="flex items-center justify-between">
      <label className="text-sm text-foreground">{label}</label>
      <button onClick={() => !readOnly && onChange(!value)} disabled={readOnly}
        className={`relative w-10 h-5 rounded-full transition-colors ${value ? 'bg-primary' : 'bg-muted'} disabled:opacity-60`}>
        <span className={`absolute top-0.5 w-4 h-4 rounded-full bg-white shadow transition-transform ${value ? 'translate-x-5' : 'translate-x-0.5'}`} />
      </button>
    </div>
  );

  return (
    <div className="space-y-6">
      {/* Investor Allocation Table */}
      <div className="bg-card border border-border rounded-xl p-5">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-semibold text-foreground flex items-center gap-2">
            <Icon name="Users" size={16} color="var(--color-primary)" />
            Investor Allocation
          </h3>
          {!readOnly && (
            <button onClick={addInvestor} className="flex items-center gap-1 text-sm text-primary hover:text-primary/80 font-medium">
              <Icon name="Plus" size={14} /> Add Investor
            </button>
          )}
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border">
                {['Investor Name', 'Type', 'Commitment (£m)', '% of Equity', 'Ticket Type', 'Status', 'Notes']?.map((h) => (
                  <th key={h} className="px-3 py-2 text-left text-xs font-semibold text-muted-foreground">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {investors?.map((inv) => (
                <tr key={inv?.id} className="hover:bg-muted/20">
                  <td className="px-3 py-2 font-medium text-foreground">{inv?.name}</td>
                  <td className="px-3 py-2 text-muted-foreground">{inv?.type}</td>
                  <td className="px-3 py-2 font-semibold">£{inv?.commitment}m</td>
                  <td className="px-3 py-2">{inv?.pctEquity}%</td>
                  <td className="px-3 py-2 text-muted-foreground">{inv?.ticketType}</td>
                  <td className="px-3 py-2">
                    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold ${STATUS_CONFIG?.[inv?.status] || 'bg-slate-100 text-slate-600'}`}>
                      {inv?.status}
                    </span>
                  </td>
                  <td className="px-3 py-2 text-muted-foreground text-xs">{inv?.notes}</td>
                </tr>
              ))}
              <tr className="border-t-2 border-border bg-muted/30 font-semibold">
                <td className="px-3 py-2 text-foreground">Total</td>
                <td />
                <td className="px-3 py-2 text-foreground">£{totalCommitted}m</td>
                <td className="px-3 py-2 text-foreground">{investors?.reduce((s, i) => s + i?.pctEquity, 0)?.toFixed(1)}%</td>
                <td colSpan={3} />
              </tr>
            </tbody>
          </table>
        </div>

        {/* Progress Bar */}
        <div className="mt-4">
          <div className="flex items-center justify-between text-xs text-muted-foreground mb-1">
            <span>Committed vs Target Raise (£{targetRaise}m)</span>
            <span>{totalPct?.toFixed(0)}%</span>
          </div>
          <div className="w-full h-3 bg-muted rounded-full overflow-hidden">
            <div className="h-full bg-primary rounded-full transition-all duration-500" style={{ width: `${totalPct}%` }} />
          </div>
          <div className="flex gap-4 mt-2 text-xs">
            <span className="text-amber-600">Soft-circle: £{softCircled}m</span>
            <span className="text-[#3b4c4c]">Hard-commit: £{hardCommitted}m</span>
            <span className="text-emerald-600">Signed: £{signed}m</span>
          </div>
        </div>
      </div>
      {/* Syndication Status */}
      <div className="bg-card border border-border rounded-xl p-5">
        <h3 className="font-semibold text-foreground mb-4 flex items-center gap-2">
          <Icon name="GitMerge" size={16} color="var(--color-primary)" />
          Syndication Status
        </h3>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
          {[
            { label: 'Target Raise', value: `£${targetRaise}m`, color: 'text-foreground' },
            { label: 'Soft-circled', value: `£${softCircled}m`, color: 'text-amber-600' },
            { label: 'Hard-committed', value: `£${hardCommitted}m`, color: 'text-[#3b4c4c]' },
            { label: 'Signed', value: `£${signed}m`, color: 'text-emerald-600' },
          ]?.map((m) => (
            <div key={m?.label} className="bg-muted/30 rounded-lg p-3 text-center">
              <p className={`text-xl font-bold ${m?.color}`}>{m?.value}</p>
              <p className="text-xs text-muted-foreground">{m?.label}</p>
            </div>
          ))}
        </div>
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          <Icon name="Clock" size={12} />
          <span>Estimated 45 days to closing — Sample data</span>
        </div>
      </div>
      {/* Co-investment Terms */}
      <div className="bg-card border border-border rounded-xl p-5">
        <h3 className="font-semibold text-foreground mb-4 flex items-center gap-2">
          <Icon name="FileText" size={16} color="var(--color-primary)" />
          Co-investment Terms
        </h3>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="space-y-4">
            <Toggle value={proRata} onChange={setProRata} label="Pro-rata Rights" />
            <Toggle value={tagAlong} onChange={setTagAlong} label="Tag-along / Drag-along" />
            <Toggle value={rofr} onChange={setRofr} label="ROFR (Right of First Refusal)" />
            <div>
              <label className="text-xs text-muted-foreground">Information Rights Level</label>
              <select value={infoRights} onChange={(e) => !readOnly && setInfoRights(e?.target?.value)} disabled={readOnly}
                className="w-full mt-1 px-3 py-1.5 text-sm border border-border rounded-lg bg-card text-foreground focus:outline-none disabled:opacity-60">
                {['Full', 'Limited', 'None']?.map((o) => <option key={o} value={o}>{o}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs text-muted-foreground">Board Representation</label>
              <select value={boardRep} onChange={(e) => !readOnly && setBoardRep(e?.target?.value)} disabled={readOnly}
                className="w-full mt-1 px-3 py-1.5 text-sm border border-border rounded-lg bg-card text-foreground focus:outline-none disabled:opacity-60">
                {['Seat', 'Observer', 'None']?.map((o) => <option key={o} value={o}>{o}</option>)}
              </select>
            </div>
          </div>
          <div>
            <label className="text-xs text-muted-foreground">Transfer Restrictions</label>
            <textarea
              value={transferRestrictions}
              onChange={(e) => !readOnly && setTransferRestrictions(e?.target?.value)}
              disabled={readOnly}
              rows={6}
              className="w-full mt-1 px-3 py-2 text-sm border border-border rounded-lg bg-card text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 resize-none disabled:opacity-60"
            />
          </div>
        </div>
      </div>
    </div>
  );
}
